import {
	Infobox,
	Layout,
	List,
	Scheme_fromType,
} from "@npco/zeller-design-system";
import * as Icon from "@npco/zeller-design-system/icon";
import * as Illustration from "@npco/zeller-design-system/illustration";
import { ADT } from "pb.adt";
import { useMemo } from "react";

import { formatCurrency, formatTimestamp } from "../../../../../@lib-i18n";
import { Layout as ModalLayout } from "../../../shared/layout";
import { Prompt } from "../../../shared/prompt";
import type { Page } from "../page";

export function PaymentFlow({
	page,
	defaultLayoutProps,
}: {
	page: Omit<ADT.Pick<Page, "PaymentFlow">, "$type">;
	defaultLayoutProps: Partial<ModalLayout.Props>;
}) {
	const { items, approved, allDeclined } = useMemo<{
		items: List.Props["items"];
		approved: { amount: string } | undefined;
		allDeclined: boolean;
	}>(() => {
		const items: Array<
			List.Props["items"] extends ReadonlyArray<infer Item> ? Item : never
		> = [];

		const skeleton: List.ItemSkeleton = {
			skeleton: true,
			avatar: true,
			description: true,
			extension: { status: true },
		};

		if (page.transactions.length === 0 || page.state.$type === "Processing") {
			items.push(skeleton);
		}

		let approved: { amount: string } | undefined = undefined;
		let allDeclined = !!page.transactions.length;
		page.transactions.forEach((transaction) => {
			if (transaction.status === "approved") {
				// Any "approved" transactions means they can't all be declined.
				allDeclined = false;
				// Last "approved" transaction wins.
				approved = {
					amount: formatCurrency({ amountInCents: transaction.amount }),
				};
			}

			items.push({
				id: transaction.id,
				label: `Sale ••• ${transaction.maskedPan?.replaceAll("…", "") || "XXXX"}`,
				avatar: Scheme_fromType(transaction.scheme).icon,
				description: formatTimestamp(transaction.timestamp),
				extension: {
					label: formatCurrency({ amountInCents: transaction.amount }),
					status:
						transaction.status === "approved"
							? {
									label: "Approved",
									color: "green",
								}
							: {
									label: "Declined",
									color: "red",
								},
				},
			});
		});

		if (page.viewedTimestamp) {
			items.push({
				id: "viewed",
				label: "Link Viewed",
				description: formatTimestamp(page.viewedTimestamp),
				avatar: { icon: <Icon.Eye />, color: false },
			});
		}

		return { items, approved, allDeclined };
	}, [page.transactions, page.state, page.viewedTimestamp]);

	return (
		<ModalLayout
			{...(defaultLayoutProps as ModalLayout.Props)}
			{...(page.state.$type === "Approved"
				? {
						actions: [
							{ label: "Done", onClick: page.state.onDone, primary: true },
						],
						onClose: page.state.onDone,
						timeout: { onTimeout: page.state.onDone },
					}
				: {
						actions: [{ label: "Cancel", onClick: page.state.onCancel }],
						onClose: page.state.onCancel,
						timeout: !page.cancelling
							? {
									// Delegate the timeout callback to the logic layer.
									onTimeout: true,
									duration: page.state.timeoutDuration,
									visibility: "always",
									label: "Expires in %",
								}
							: undefined,
						pending: page.cancelling ? "close" : undefined,
					})}
		>
			<Layout.Container center>
				<Layout.Row>
					<div className="flex flex-1 pl-32 pr-4">
						{approved ? (
							<Prompt
								image={<Illustration.Ok />}
								title={`${approved.amount} Approved`}
							/>
						) : (
							<Prompt
								image={<Illustration.Hourglass />}
								title="Waiting for Payment"
								// Maybe this will come back.
								// message={page.ecommerceReference}
							/>
						)}
					</div>
					<div className="flex flex-col flex-1 pr-32 pl-4 gap-24">
						<List items={items} />
						{page.state.$type !== "Processing" && allDeclined && (
							<Infobox
								title="Payment Declined"
								message="Please ask your customer to try again."
								type="danger"
							/>
						)}
					</div>
				</Layout.Row>
			</Layout.Container>
		</ModalLayout>
	);
}
